import type {RentalCalendarVehicleResponseBody} from '@dms/api/vehicle';

import {Nullish} from 'shared/web';

import {CALENDAR_CONSTANTS} from '../constants/calendarConstants';
import type {RentalCalendarDayGroupLane, RentalCalendarRow} from '../types/RentalCalendarRow';
import type {RentalCalendarScale} from '../types/RentalCalendarScale';
import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {aggregateSameDayEvents} from './aggregateSameDayEvents';
import {detectEventCollisions} from './detectEventCollisions';
import {filterEventsForRange} from './filterEventsForRange';
import {getEventCollisionLayout} from './getEventCollisionLayout';

/**
 * Builds the calendar rows: every rental vehicle becomes a row (vehicles without reservations
 * included), sorted by title. Crowded days collapse into chips, and chips take part in the lane
 * layout alongside the bars, so nothing overlaps and the row is only as tall as what it draws.
 */
export function buildCalendarRows(
  vehicles: RentalCalendarVehicleResponseBody[] | Nullish,
  events: RentalResourceCalendarEvent[],
  scale: RentalCalendarScale
): RentalCalendarRow[] {
  const visibleEvents = filterEventsForRange(events, scale.rangeStart, scale.rangeEnd);

  const eventsByVehicleId = visibleEvents.reduce<Map<string, RentalResourceCalendarEvent[]>>(
    (map, event) => {
      const vehicleEvents = map.get(event.vehicleId);
      if (vehicleEvents) {
        vehicleEvents.push(event);
      } else {
        map.set(event.vehicleId, [event]);
      }
      return map;
    },
    new Map()
  );

  return (vehicles ?? [])
    .map((vehicle): RentalCalendarRow => {
      const vehicleEvents = eventsByVehicleId.get(vehicle.vehicleId) ?? [];
      const {bars, groups} = aggregateSameDayEvents(vehicleEvents, scale);

      // Each chip occupies its whole day column, so it competes for a lane like any bar
      const chips = groups.map((group) => ({
        group,
        placeholder: {
          ...group.events[0],
          id: `${CHIP_PLACEHOLDER_ID_PREFIX}${group.columnIndex}`,
          start: group.day,
          end: scale.columns.at(group.columnIndex + 1) ?? scale.rangeEnd,
        },
      }));

      const collisionLayout = detectEventCollisions([
        ...bars,
        ...chips.map((chip) => chip.placeholder),
      ]);

      const groupLanes = chips.map(
        ({group, placeholder}): RentalCalendarDayGroupLane => ({
          ...group,
          laneIndex: getEventCollisionLayout(placeholder, collisionLayout).rowIndex,
        })
      );

      return {
        vehicle,
        events: bars,
        groups: groupLanes,
        collisionLayout,
        rowHeight: CALENDAR_CONSTANTS.ROW_HEIGHT * Math.max(collisionLayout.maxRowsNeeded, 1),
      };
    })
    .sort((a, b) => a.vehicle.title.localeCompare(b.vehicle.title));
}

const CHIP_PLACEHOLDER_ID_PREFIX = 'dayChip-';
